import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import { useEventReviews } from '@/hooks/useVendorReviews'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ErrorState, LoadingState } from '@/components/ui/states'
import { VendorProfileLink } from '@/components/VendorProfileLink'
import { ReviewDialog } from '@/components/reviews/ReviewDialog'
import { reviewerLine } from '@lib/helpers/vendor-reviews'
import { Stars } from '@/components/reviews/ShownReviews'
import { VENDOR_SERVICE_LABELS } from '@lib/enums/vendor-service.enum'
import type { VendorReviewStatus } from '@lib/enums/vendor-review-status.enum'
import type { EventReviewTargetDto } from '@lib/dto/vendor-review.dto'

/** Where a review stands, as its writer reads it. */
const STATUS: Record<VendorReviewStatus, { label: string; tone: BadgeTone }> = {
  pending: { label: 'Waiting for approval', tone: 'warning' },
  approved: { label: 'Published', tone: 'success' },
  declined: { label: 'Not published', tone: 'danger' },
  removed: { label: 'Taken down', tone: 'neutral' },
}

interface RowProps {
  target: EventReviewTargetDto
  /** Reviews may be written: the event was booked and is over. */
  open: boolean
  onWrite: () => void
}

/** One company on the event, and the reader's review of it. */
function Row({ target, open, onWrite }: RowProps) {
  const mine = target.mine
  const changeable = open && !target.closed && (mine === null || mine.status === 'pending' || mine.status === 'declined')
  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-2 px-4 py-3 text-sm">
      <div className="min-w-0 flex-1 basis-48 space-y-0.5">
        <span className="block font-medium">
          <VendorProfileLink vendorId={target.vendorId} name={target.companyName} />
        </span>
        <span className="block text-xs text-ink-muted">{target.services.map((s) => VENDOR_SERVICE_LABELS[s]).join(', ')}</span>
      </div>
      {mine === null ? null : (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Stars rating={mine.rating} className="tabular-nums" />
            <Badge tone={STATUS[mine.status].tone}>{STATUS[mine.status].label}</Badge>
          </div>
          {mine.writtenBy === null ? null : <p className="m-0 text-xs text-ink-muted">Written by {mine.writtenBy}</p>}
          {mine.status === 'declined' && mine.declineReason !== null ? (
            <p className="m-0 max-w-sm text-xs text-ink-muted">&ldquo;{mine.declineReason}&rdquo;</p>
          ) : null}
          {target.closed ? <p className="m-0 text-xs text-ink-muted">No longer open to reviews</p> : null}
        </div>
      )}
      {changeable ? (
        <Button size="sm" variant="outline" onClick={onWrite}>
          {mine === null ? 'Write a review' : 'Change'}
        </Button>
      ) : null}
    </li>
  )
}

interface Props {
  eventId: string
  /** The planner reads its own wording; the couple, theirs. */
  planner: boolean
}

/**
 * Reviewing the companies that worked the event, once it's over
 * (docs/vendor-growth.spec.md, "Reviews"): on the couple's event page, and on
 * their planner's, which is where a planner is asked. Each row says where its
 * review stands; one waiting or declined can be changed while reviews are open.
 * Whether they are is the server's answer, not this page's copy of the event.
 *
 * The couple's email asking for reviews links here with `?section=reviews`,
 * so the section brings itself into view once it has loaded.
 */
export function EventReviews({ eventId, planner }: Props) {
  const reviews = useEventReviews(eventId)
  const [params] = useSearchParams()
  const [writing, setWriting] = React.useState<EventReviewTargetDto | null>(null)
  const section = React.useRef<HTMLDivElement>(null)
  const asked = params.get('section') === 'reviews'
  const loaded = reviews.isSuccess

  React.useEffect(() => {
    if (asked && loaded) section.current?.scrollIntoView({ block: 'start' })
  }, [asked, loaded])

  function body() {
    if (reviews.isPending) return <LoadingState label="Loading reviews…" />
    if (reviews.isError) return <ErrorState message={requestErrorMessage(reviews.error)} onRetry={() => void reviews.refetch()} />
    const { open, shownAs, month, vendors } = reviews.data
    const shown = month === null ? shownAs.name : reviewerLine(shownAs, month)
    return (
      <>
        <p className="m-0 text-sm text-ink-muted">
          {planner ? 'Tell couples and venues how the event’s vendors did.' : 'Tell other couples how your vendors did.'} FLOH Venues checks each review
          before it shows on the vendor&rsquo;s page, as &ldquo;{shown}&rdquo;.
        </p>
        {vendors.length === 0 ? (
          <p className="m-0 text-sm text-ink-muted">No vendors worked this event.</p>
        ) : (
          <ul className="m-0 list-none divide-y divide-line overflow-hidden rounded-md border border-line p-0">
            {vendors.map((target) => (
              <Row key={target.vendorId} target={target} open={open} onWrite={() => setWriting(target)} />
            ))}
          </ul>
        )}
        {writing === null ? null : (
          <ReviewDialog
            eventId={eventId}
            vendorId={writing.vendorId}
            companyName={writing.companyName}
            shownAs={shown}
            existing={writing.mine}
            current={vendors.find((target) => target.vendorId === writing.vendorId)?.mine ?? null}
            onClose={() => setWriting(null)}
          />
        )}
      </>
    )
  }

  return (
    <div id="reviews" ref={section} className="scroll-mt-6 space-y-2">
      <p className="text-sm font-medium">Reviews</p>
      {body()}
    </div>
  )
}
